import React, { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import DateRangePickerComponent from "./CustomDateRangePicker";
import moment from "moment";
import { useTable, useGlobalFilter } from "react-table";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const FuelConsumptionReport = ({ accessOfDepartments }) => {
    const [itemOptions, setItemOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);

    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState([]);

    const [dateRange, setDateRange] = useState([null, null]);
    const [loading, setLoading] = useState(false);

    const [reportData, setReportData] = useState(null);

    const code = localStorage.getItem("code");
    const token = localStorage.getItem("token");

    // -----------------------------------------------------
    // Fetch Items
    // -----------------------------------------------------
    const fetchItemOptions = async () => {
        try {
            const res = await apiClient.get(`${SERVER_BASE}/getSelectiveGroupItem`, {
                params: { code, groupCode: ["O"] },
                headers: { Authorization: `Bearer ${token}` }
            });

            setItemOptions(
                res.data.map((item) => ({
                    value: item.printName,
                    label: `${item.printName}${item.additionalNames ? ` - [ ${item.additionalNames} ]` : ""}`,
                    itemId: item.id,
                    uom: item.uom,
                }))
            );
        } catch (err) {
            console.error("Item Fetch Error:", err);
        }
    };

    useEffect(() => {
        fetchItemOptions();
    }, []);

    // -----------------------------------------------------
    // Convert departments
    // -----------------------------------------------------
    useEffect(() => {
        if (accessOfDepartments) {
            setDepartments(
                accessOfDepartments.split(",").map((d) => ({
                    value: d.trim(),
                    label: d.trim(),
                }))
            );
        }
    }, [accessOfDepartments]);

    // -----------------------------------------------------
    // GET REPORT
    // -----------------------------------------------------
    const handleGetReport = async () => {
        if (!selectedItem) {
            Swal.fire({
                icon: "warning",
                text: "Please select an item!",
                toast: true,
                position: "top-end",
                timer: 1500,
                showConfirmButton: false,
            });
            return;
        }

        if (!selectedDept || selectedDept.length === 0) {
            Swal.fire({
                icon: "warning",
                text: "Please select at least one department!",
                toast: true,
                position: "top-end",
                timer: 1500,
                showConfirmButton: false,
            });
            return;
        }

        const [startDate, endDate] = dateRange;

        if (!startDate || !endDate) {
            Swal.fire({
                icon: "warning",
                text: "Please select a valid date range!",
                toast: true,
                position: "top-end",
                timer: 1500,
                showConfirmButton: false,
            });
            return;
        }

        const payload = {
            itemId: selectedItem.itemId,
            startDate,
            endDate,
            departments: selectedDept.map((d) => d.value),
        };

        try {
            setLoading(true);

            const res = await apiClient.get(`${SERVER_BASE}/getFuelConsumptionReport`, {
                params: payload,
                headers: { Authorization: `Bearer ${token}` },
            });

            setReportData(res.data);
        } catch (err) {
            console.error("Fetch Error:", err);
            Swal.fire("Error", "Failed to fetch report", "error");
        } finally {
            setLoading(false);
        }
    };
    const handleExportExcel = async () => {
        if (!tableData.length) {
            Swal.fire({
                icon: "warning",
                title: "No data to export",
                toast: true,
                position: "top-end",
                timer: 2000,
                showConfirmButton: false,
            });
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Fuel Consumption");

        // ================= HEADER INFO =================
        worksheet.addRow(["Fuel Consumption Report"]).font = { bold: true };
        worksheet.addRow([
            `Item: ${selectedItem?.label || "-"}`,
            `From: ${moment(dateRange[0]).format("DD-MMM-YYYY")}`,
            `To: ${moment(dateRange[1]).format("DD-MMM-YYYY")}`,
        ]);
        worksheet.addRow([]);

        // ================= TABLE HEADER =================
        const headerRow = worksheet.addRow([
            "S.No",
            "Allocation",
            "Date",
            "Quantity",
            "UOM",
        ]);

        headerRow.font = { bold: true };

        // ================= DATA =================
        tableData.forEach((row) => {
            if (row.isGrandTotal) {
                worksheet.addRow([
                    "",
                    "Grand Total",
                    "",
                    row.qty[0],
                    row.uom[0],
                ]).font = { bold: true };
                return;
            }

            row.date.forEach((date, idx) => {
                const isTotalRow = date === "Total";

                worksheet.addRow([
                    idx === 0 ? row.sNo : "",
                    idx === 0 ? row.allocation : "",
                    date,
                    row.qty[idx],
                    row.uom[idx],
                ]).font = isTotalRow ? { bold: true } : {};
            });
        });

        // ================= AUTO WIDTH =================
        worksheet.columns.forEach((col) => {
            col.width = Math.max(12, col.header?.length || 12);
        });

        // ================= DOWNLOAD =================
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `Fuel_Consumption_${selectedItem?.value || "Item"}_${moment().format(
                "DDMMYYYY_HHmm"
            )}.xlsx`
        );
    };

    // -----------------------------------------------------
    // Build table rows with totals inside each group
    // -----------------------------------------------------
    const tableData = React.useMemo(() => {
        if (!reportData || !reportData.groupedByAllocation) return [];

        const result = [];
        const groups = reportData.groupedByAllocation;

        let grandTotalQty = 0;

        Object.keys(groups).forEach((alloc, idx) => {
            const rows = groups[alloc];

            const dateList = rows.map((r) =>
                moment(r.affectedDate).format("DD-MMM-YYYY")
            );

            const qtyList = rows.map((r) => {
                const qty = Number(r.quantity);
                return qty % 1 === 0 ? qty : qty.toFixed(2);
            });

            const uomList = rows.map(() => selectedItem?.uom || "-");

            // Calculate total
            const allocationTotal = qtyList.reduce((sum, q) => sum + Number(q), 0);
            grandTotalQty += allocationTotal;

            // Push totals inside same row
            dateList.push("Total");
            qtyList.push(allocationTotal.toFixed(2));
            uomList.push(selectedItem?.uom || "-");

            result.push({
                sNo: idx + 1,
                allocation: alloc,
                date: dateList,
                qty: qtyList,
                uom: uomList,
                isGrandTotal: false,
            });
        });

        // Add Grand Total row
        result.push({
            sNo: "",
            allocation: "Grand Total",
            date: [""],
            qty: [grandTotalQty.toFixed(2)],
            uom: [selectedItem?.uom || "-"],
            isGrandTotal: true,
        });

        return result;
    }, [reportData, selectedItem]);

    // -----------------------------------------------------
    // Table Columns
    // -----------------------------------------------------
    const columns = React.useMemo(
        () => [
            { Header: "S.No", accessor: "sNo" },
            { Header: "Allocation", accessor: "allocation" },

            {
                Header: "Date",
                accessor: "date",
                Cell: ({ row, value }) =>
                    row.original.isGrandTotal ? (
                        <b>-</b>
                    ) : (
                        <div>
                            {value.map((d, i) => (
                                <div key={i} style={{ fontWeight: d === "Total" ? "bold" : "normal" }}>
                                    {d}
                                </div>
                            ))}
                        </div>
                    ),
            },

            {
                Header: "Qty",
                accessor: "qty",
                Cell: ({ value }) => (
                    <div>
                        {value.map((q, i) => (
                            <div key={i} style={{ fontWeight: i === value.length - 1 ? "bold" : "normal" }}>
                                {q}
                            </div>
                        ))}
                    </div>
                ),
            },

            {
                Header: "UOM",
                accessor: "uom",
                Cell: ({ value }) => (
                    <div>
                        {value.map((u, i) => (
                            <div key={i} style={{ fontWeight: i === value.length - 1 ? "bold" : "normal" }}>
                                {u}
                            </div>
                        ))}
                    </div>
                ),
            },
        ],
        []
    );

    // -----------------------------------------------------
    // React Table setup (no pagination)
    // -----------------------------------------------------
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data: tableData,
        },
        useGlobalFilter
    );

    // -----------------------------------------------------
    // UI
    // -----------------------------------------------------
    return (
        <div className="formcontainer mt-3">
            <h4 className="mb-3">Fuel Consumption</h4>

            {/* Filters */}
            <div className="row align-items-end mb-4">
                <div className="col-md-3">
                    <label className="form-label">Item</label>
                    <Select
                        options={itemOptions}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        placeholder="Select Item"
                    />
                </div>

                <div className="col-md-4">
                    <label className="form-label">Departments</label>
                    <Select
                        isMulti
                        options={departments}
                        value={selectedDept}
                        onChange={setSelectedDept}
                        placeholder="Select Department(s)"
                    />
                </div>

                <div className="col-md-3">
                    <label className="form-label">Date Range</label>
                    <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
                </div>

                {/* Get Button */}
                <div className="col-md-2 d-flex align-items-center">
                    <button
                        className="btn btn-outline-success"
                        onClick={handleGetReport}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Get"}
                    </button>

                    <button
                        className="btn btn-outline-primary ms-2"
                        onClick={handleExportExcel}
                        disabled={!tableData.length}
                    >
                        Export
                    </button>
                </div>

            </div>

            {/* Table */}

            <div className="mt-4">
                <input
                    className="form-control mb-3"
                    placeholder="Search..."
                    value={state.globalFilter || ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                />

                <table className="table table-striped table-bordered" {...getTableProps()}>
                    <thead>
                        {headerGroups.map((hg) => (
                            <tr {...hg.getHeaderGroupProps()}>
                                {hg.headers.map((col) => (
                                    <th {...col.getHeaderProps()}>{col.render("Header")}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default FuelConsumptionReport;
